import { MAP_SIZE, TILE_SIZE } from '../game/worldConstants';

export interface CanvasDimensions {
  canvasWidth: number;
  canvasHeight: number;
}

export type CanvasDimensionUnit = 'pixels' | 'tiles';

export const DEFAULT_LEVEL_CANVAS_WIDTH = MAP_SIZE;
export const DEFAULT_LEVEL_CANVAS_HEIGHT = MAP_SIZE;

export const normalizeCanvasDimension = (
  value: number | null | undefined,
  fallback = MAP_SIZE,
): number => {
  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return Math.max(1, Math.floor(numericValue));
  }
  return Math.max(1, Math.floor(fallback));
};

export const tilesToPixels = (tiles: number, tileSize = TILE_SIZE): number =>
  Math.max(1, normalizeCanvasDimension(tiles, 1) * tileSize);

export const pixelsToTiles = (pixels: number | null | undefined, tileSize = TILE_SIZE): number => {
  const numericValue = Number(pixels);
  if (!Number.isFinite(numericValue)) {
    return 1;
  }
  return Math.max(1, Math.ceil(numericValue / tileSize));
};

export const normalizeCanvasDimensions = (
  canvasWidth: number | null | undefined,
  canvasHeight: number | null | undefined,
  fallbackWidth = DEFAULT_LEVEL_CANVAS_WIDTH,
  fallbackHeight = DEFAULT_LEVEL_CANVAS_HEIGHT,
): CanvasDimensions => ({
  canvasWidth: normalizeCanvasDimension(canvasWidth, fallbackWidth),
  canvasHeight: normalizeCanvasDimension(canvasHeight, fallbackHeight),
});

export const normalizeCanvasDimensionsFromUnit = (
  canvasWidth: number | null | undefined,
  canvasHeight: number | null | undefined,
  unit: CanvasDimensionUnit,
  fallbackWidth = DEFAULT_LEVEL_CANVAS_WIDTH,
  fallbackHeight = DEFAULT_LEVEL_CANVAS_HEIGHT,
  tileSize = TILE_SIZE,
): CanvasDimensions => {
  if (unit === 'pixels') {
    return {
      canvasWidth: pixelsToTiles(canvasWidth, tileSize),
      canvasHeight: pixelsToTiles(canvasHeight, tileSize),
    };
  }

  return normalizeCanvasDimensions(canvasWidth, canvasHeight, fallbackWidth, fallbackHeight);
};

export const hasCanvasDimensionChange = (
  currentCanvasWidth: number,
  currentCanvasHeight: number,
  nextCanvasWidth: number,
  nextCanvasHeight: number,
): boolean => currentCanvasWidth !== nextCanvasWidth || currentCanvasHeight !== nextCanvasHeight;

export const clampCanvasOffset = (value: number, canvasSpan: number): number =>
  Math.max(0, Math.min(Math.floor(value), Math.max(0, canvasSpan - 1)));

export const describeCanvasDimensions = (
  canvasWidth: number,
  canvasHeight: number,
  tileSize = TILE_SIZE,
): { pixelsLabel: string; tilesLabel: string } => ({
  pixelsLabel: `${tilesToPixels(canvasWidth, tileSize)}x${tilesToPixels(canvasHeight, tileSize)}px`,
  tilesLabel: `${canvasWidth}x${canvasHeight} tiles`,
});

export const getCanvasResizeWarning = (
  currentCanvasWidth: number,
  currentCanvasHeight: number,
  nextCanvasWidth: number,
  nextCanvasHeight: number,
): string | null => {
  const croppedAxes: string[] = [];
  if (nextCanvasWidth < currentCanvasWidth) {
    croppedAxes.push('right edge');
  }
  if (nextCanvasHeight < currentCanvasHeight) {
    croppedAxes.push('bottom edge');
  }
  if (croppedAxes.length === 0) {
    return null;
  }

  const axisLabel =
    croppedAxes.length === 1 ? croppedAxes[0] : `${croppedAxes[0]} and ${croppedAxes[1]}`;
  return `Shrinking the canvas crops tiles, traversal data, and image offsets outside the new ${axisLabel}. The top-left origin stays fixed.`;
};

export const getRecommendedCanvasDimensionsFromImage = ({
  imageWidth,
  imageHeight,
  tileSize,
}: {
  imageWidth: number;
  imageHeight: number;
  tileSize: number;
}): CanvasDimensions =>
  normalizeCanvasDimensions(Math.ceil(imageWidth / tileSize), Math.ceil(imageHeight / tileSize));
